import { useRef, useState } from 'react';
import { ImageOff, Pause, Play, Trash } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LibraryCreative } from '../AssetLibrary/hooks';

interface Props {
  asset: LibraryCreative;
  readOnly: boolean;
  removeAsset: () => void;
}

export function AssetDisplay({ asset, readOnly, removeAsset }: Props) {
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const video = asset.assets.find((a) => a.type === 'video');

  function toggleVideo() {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }

  return (
    <div className="bg-muted relative flex h-100 items-center justify-center rounded-xl p-4">
      <p className="absolute top-4 left-4 text-sm font-medium">
        Duration: {asset.duration_ms / 1_000} seconds
      </p>
      {!readOnly && (
        <Button
          aria-label="delete"
          className="absolute top-3 right-3"
          onClick={removeAsset}
          size="icon"
          variant="outline"
        >
          <Trash />
        </Button>
      )}
      {video ? (
        <div
          className="flex items-center justify-center"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            className={cn(
              'relative flex items-center justify-center',
              asset.orientation === 'PORTRAIT' ? 'h-80 w-45' : 'h-45 w-80',
            )}
          >
            <video
              className="size-auto object-contain p-1"
              muted
              onPause={() => setPlaying(false)}
              onPlay={() => setPlaying(true)}
              ref={videoRef}
            >
              <source src={video.url} type="video/mp4" />
              Your browser does not support the video tag
            </video>
            <Button
              aria-label={playing ? 'pause' : 'play'}
              data-testid="toggle-video"
              className={cn(
                'absolute top-1/2 left-1/2 -translate-1/2',
                playing && !hovered ? 'opacity-0' : 'opacity-100',
              )}
              onClick={toggleVideo}
              size="icon"
              type="button"
              variant="outline"
            >
              <Play className={cn(playing ? 'hidden' : 'inline-block')} />
              <Pause
                className={cn(playing && hovered ? 'inline-block' : 'hidden')}
              />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex h-45 w-80 items-center justify-center rounded bg-gray-300">
          <ImageOff className="size-5" />
        </div>
      )}
    </div>
  );
}
